import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Phone, Check, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { resolveConsultSource } from '../lib/consultSource';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const timeSlots = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
];

const projectTypes = [
  { id: 'residential', name: 'Residential', description: 'Home or apartment project' },
  { id: 'commercial', name: 'Commercial', description: 'Office, retail, or hospitality' },
  { id: 'new-construction', name: 'New Construction', description: 'Building or renovation project' },
];

export const ConsultationPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_type: '',
    preferred_date: null,
    preferred_time: '',
    address: '',
    message: '',
  });
  const [source] = useState(resolveConsultSource);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        ...source,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, 'yyyy-MM-dd') : null,
      };
      
      await axios.post(`${API_URL}/consultation/request`, submitData);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Consultation request submitted!');
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.project_type;
      case 2: return formData.preferred_date && formData.preferred_time;
      case 3: return formData.name && formData.email && formData.phone;
      default: return true;
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6 pt-32" data-testid="consultation-success">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg w-full text-center"
          >
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-serif mb-4">Consultation Requested!</h1>
            <p className="text-neutral-600 mb-8">
              Thank you for scheduling a consultation. Our team will contact you within 24 hours to confirm your appointment.
            </p>
            
            <div className="bg-neutral-50 p-6 rounded-lg text-left mb-8">
              <h3 className="font-medium mb-4">Your Request Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-neutral-500">Project Type:</span> <span className="capitalize">{formData.project_type.replace(/-/g, " ")}</span></p>
                <p><span className="text-neutral-500">Preferred Date:</span> {formData.preferred_date ? format(formData.preferred_date, 'MMMM d, yyyy') : '-'}</p>
                <p><span className="text-neutral-500">Preferred Time:</span> {formData.preferred_time}</p>
                <p><span className="text-neutral-500">Contact:</span> {formData.email}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => window.location.href = '/products'} className="btn-primary flex-1">
                Browse Products
              </Button>
              <Button onClick={() => window.location.href = '/'} variant="outline" className="flex-1">
                Return Home
              </Button>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <button 
          onClick={() => window.history.back()} 
          className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </button>
      </div>

      {/* Hero */}
      <section className="pt-8 pb-16 bg-neutral-50">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="label-overline text-amber-700">Free Consultation</span>
            <h1 className="mt-4 text-neutral-900">Schedule Your Consultation</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              Book a free in-home or virtual consultation with our window treatment experts. We'll help you find the perfect solution for your space.
            </p>
            <p className="mt-4 text-neutral-600" data-testid="consultation-service-area-note">
              We visit homes and businesses across NYC, Long Island, Westchester and northern New Jersey.{' '}
              <Link to="/service-area" className="text-amber-700 underline underline-offset-4 hover:text-amber-800" data-testid="consultation-service-area-link">See our service area</Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding" data-testid="consultation-form">
        <div className="container-lg">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Steps Indicator */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="space-y-4">
                  {[
                    { num: 1, title: 'Project Type', desc: 'Tell us about your project' },
                    { num: 2, title: 'Date & Time', desc: 'Choose your preferred slot' },
                    { num: 3, title: 'Your Details', desc: 'Complete your booking' },
                  ].map((s) => (
                    <div
                      key={s.num}
                      className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                        step === s.num ? 'bg-neutral-900 text-white' : step > s.num ? 'bg-amber-50' : 'bg-neutral-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                        step === s.num ? 'bg-white text-neutral-900' : step > s.num ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-600'
                      }`}>
                        {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                      </div>
                      <div>
                        <p className={`font-medium ${step === s.num ? 'text-white' : 'text-neutral-900'}`}>{s.title}</p>
                        <p className={`text-sm ${step === s.num ? 'text-white/70' : 'text-neutral-500'}`}>{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div className="mt-8 p-6 bg-amber-50 rounded-lg">
                  <h4 className="font-medium mb-4">What's Included</h4>
                  <ul className="space-y-3 text-sm">
                    {[
                      'Expert measurement service',
                      'Product recommendations',
                      'Instant price estimates',
                      'Fabric samples to take home',
                      'No obligation to purchase'
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-amber-600" />
                        <span className="text-neutral-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-lg p-6 lg:p-8">
                {/* Step 1: Project Type */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-serif mb-2">What type of project is this?</h2>
                      <p className="text-neutral-600">Select the option that best describes your project</p>
                    </div>

                    <div className="space-y-3">
                      {projectTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, project_type: type.id })}
                          className={`w-full p-4 border rounded-lg text-left transition-all ${
                            formData.project_type === type.id
                              ? 'border-neutral-900 bg-neutral-50'
                              : 'border-neutral-200 hover:border-neutral-400'
                          }`}
                          data-testid={`project-type-${type.id}`}
                        >
                          <p className="font-medium">{type.name}</p>
                          <p className="text-sm text-neutral-500">{type.description}</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Date & Time */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-serif mb-2">When works best for you?</h2>
                      <p className="text-neutral-600">Select your preferred date and time</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-sm font-medium mb-3 block">Preferred Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal h-12">
                              <Calendar className="mr-2 h-4 w-4" />
                              {formData.preferred_date ? format(formData.preferred_date, 'PPP') : 'Select a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent
                              mode="single"
                              selected={formData.preferred_date}
                              onSelect={(date) => setFormData({ ...formData, preferred_date: date })}
                              disabled={(date) => date < new Date() || date.getDay() === 0}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div>
                        <Label className="text-sm font-medium mb-3 block">Preferred Time</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {timeSlots.map((time) => (
                            <button
                              key={time}
                              type="button"
                              onClick={() => setFormData({ ...formData, preferred_time: time })}
                              className={`p-2 text-sm border rounded transition-all ${
                                formData.preferred_time === time
                                  ? 'border-neutral-900 bg-neutral-900 text-white'
                                  : 'border-neutral-200 hover:border-neutral-400'
                              }`}
                              data-testid={`time-slot-${time.replace(/[:\s]/g, '-')}`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Contact Details */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-serif mb-2">Your Contact Information</h2>
                      <p className="text-neutral-600">We'll use this to confirm your appointment</p>
                    </div>

                    <div className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium">Full Name *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            className="mt-1"
                            data-testid="consultation-name"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Phone *</Label>
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            required
                            className="mt-1"
                            data-testid="consultation-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-1"
                          data-testid="consultation-email"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Project Address (Optional)</Label>
                        <Input
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className="mt-1"
                          placeholder="Street address, city, state, zip"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Additional Notes (Optional)</Label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="mt-1"
                          rows={3}
                          placeholder="Tell us about your project, special requirements, or questions..."
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}

                  {step < 3 ? (
                    <Button
                      type="button"
                      onClick={() => setStep(step + 1)}
                      disabled={!canProceed()}
                      className="btn-primary"
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={loading || !canProceed()}
                      className="btn-primary"
                      data-testid="submit-consultation"
                    >
                      {loading ? 'Submitting...' : 'Book Consultation'}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-neutral-50">
        <div className="container-xl">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Phone, title: 'Call Us', info: '(212) 209-0654', desc: 'Showroom visits by appointment' },
              { icon: MapPin, title: 'Visit Showroom', info: '1 Brooklyn Navy Yard, Building 33, Suite 201B', desc: 'Brooklyn, NY 11205' },
              { icon: Clock, title: 'Response Time', info: 'Within 24 Hours', desc: 'We\'ll confirm your booking' },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <item.icon className="w-5 h-5 text-amber-700" />
                </div>
                <h4 className="font-medium mb-1">{item.title}</h4>
                <p className="text-neutral-900">{item.info}</p>
                <p className="text-sm text-neutral-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};
